// React & routing
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

// State management
import { useLotStore } from '@/stores/useLot.store';

// API services
import { addProcessStep, changeLotStage, getLotById } from '@/network/api.service';

// UI components
import LotCard from '@/components/lot/LotCard';
import { Button } from '@/components/shadcn-ui/button';
import { Card } from '@/components/shadcn-ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn-ui/select';

// Icons
import { CircleCheck, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';

// Utilities / helpers
import { toast } from 'sonner';
import Loader from '@/components/sharedComponents/Loader';

const DefinedProcessStepPage = () => {
  const location = useLocation();
  const fromScreen = location.state?.from || 'unknown';

  const [fetchLoading, setFetchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [lot, setLot] = useState();
  const lot = useLotStore(state => state.lot);

  const [showAddStep, setShowAddStep] = useState(false);
  const [selectedStepName, setSelectedStepName] = useState('');
  const [savingStep, setSavingStep] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchLot = async () => {
    setFetchLoading(true);
    const { data } = await getLotById(id, false);

    if (data?.data) {
      // setLot(data.data);
      useLotStore.getState().setLot({ ...data.data });
    }
    setFetchLoading(false);
  };

  useEffect(() => {
    fetchLot();
  }, []);

  const handleMoveToDrying = async () => {
    try {
      setLoading(true);
      const { data } = await changeLotStage(id, { stage: 'Drying' });
      if (data?.data) {
        toast.success('Lot moved to drying');
        navigate(fromScreen === 'lot-detail' ? -1 : `/`, { replace: true });
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const areAllStepsCompleted = lot?.processing_steps?.every(step => step.status === 'Completed') ?? false;

  const handleAddStepClick = () => {
    setShowAddStep(true);
    setSelectedStepName('');
  };

  const handleStepSelect = async stepName => {
    if (!stepName) return;

    setSelectedStepName(stepName);
    setSavingStep(true);

    try {
      // Calculate next sequence number
      const nextSequence = (lot?.processing_steps?.length ?? 0) + 1;

      // Prepare the step data
      const stepData = {
        sequence: nextSequence,
        name: stepName,
        status: 'Yet to start',
      };

      const { data } = await addProcessStep(id, stepData);

      if (data?.success) {
        toast.success('Processing step added successfully');
        // Refresh the lot data
        await fetchLot();
        // Reset the form
        setShowAddStep(false);
        setSelectedStepName('');
      } else {
        toast.error('Failed to add processing step');
      }
    } catch (error) {
      console.error('Error adding step:', error);
      toast.error(error?.response?.data?.message || 'Failed to add processing step');
    } finally {
      setSavingStep(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col space-y-[18px] px-4 py-[18px]">
      <LotCard lot={lot} edit={true} />

      <div className="">
        <div className="flex items-center justify-between px-1">
          <div className="mb-1 font-medium">Type of processing</div>
          <div className="mr-2 h-[18px] w-[18px]">
            <Pencil className="h-full w-full" />
          </div>
        </div>
        <Card className="mt-1 rounded-[10px] p-4 text-sm font-medium">{lot?.type_of_processing}</Card>
      </div>

      <div className="">
        <div className="flex items-center justify-between px-1">
          <div className="mb-1 font-medium">Processing steps</div>
          <div className="mr-2 h-[18px] w-[18px]">
            <Pencil className="h-full w-full" />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          {lot?.processing_steps?.map((process_data, idx) => {
            // Find previous step by sequence
            const previousStep = lot?.processing_steps?.find(step => step.sequence === process_data.sequence - 1);

            // Check if previous step is completed or in progress
            // First step (sequence 1) is always clickable
            const isClickable = process_data.sequence === 1 || (previousStep && previousStep.status === 'Completed');

            const cardContent = (
              <Card
                className={`mt-1 flex items-center justify-between rounded-[10px] p-4 pl-10 text-sm font-medium ${
                  !isClickable ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                <div>
                  {process_data?.sequence}. {process_data?.name}
                </div>
                <div className="flex items-center gap-1 rounded-full bg-[#171A1F] px-3 py-0.5 text-xs font-medium text-white">
                  {process_data?.status === 'Completed' && <CircleCheck className="h-3 w-3" />}
                  {process_data?.status || 'Yet to start'}
                </div>
              </Card>
            );

            if (!isClickable) {
              return <div key={process_data?._id}>{cardContent}</div>;
            }

            return (
              <Link
                key={process_data?._id}
                to={
                  process_data?.status === 'Completed' || process_data?.status === 'In Progress'
                    ? `${process_data?._id}/complete`
                    : `${process_data?._id}`
                }
              >
                {cardContent}
              </Link>
            );
          })}

          {showAddStep && (
            <Card className="mt-1 rounded-[10px] p-4">
              <div className="flex items-center justify-between">
                <Select value={selectedStepName} onValueChange={handleStepSelect} disabled={savingStep}>
                  <SelectTrigger className="w-[200px] rounded-lg bg-white text-sm">
                    <SelectValue placeholder="Select step" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pulping">Pulping</SelectItem>
                    <SelectItem value="Washing">Washing</SelectItem>
                    <SelectItem value="Fermentation">Fermentation</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddStep(false);
                    setSelectedStepName('');
                  }}
                  disabled={savingStep}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </Card>
          )}

          <div className="mt-3 flex flex-col gap-2">
            {!showAddStep && (
              <Button variant="outline" className="rounded-[10px] border-dashed bg-white" onClick={handleAddStepClick}>
                <Plus className="h-4 w-4" />
                Add step
              </Button>
            )}

            <Button
              onClick={handleMoveToDrying}
              disabled={!areAllStepsCompleted || lot?.stage !== 'Process'}
              className={!areAllStepsCompleted ? 'cursor-not-allowed opacity-50' : ''}
            >
              {loading ? <Loader2 className="animate-spin h-4 w-4" /> : 'Move to drying'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefinedProcessStepPage;
